import { useState, useEffect, useId } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { getUserRoleLabel, BlocklistMatchType } from '../types';
import ConfirmModal from '../components/ConfirmModal';
import NotificationPreferences from '../components/NotificationPreferences';
import PageHeader from '../components/PageHeader';
import EmployeePrivateDetails from '../components/employees/EmployeePrivateDetails';
import ClaudeConnectPanel from '../components/claude/ClaudeConnectPanel';
import GoogleCalendarConnect from '../components/gcal/GoogleCalendarConnect';
import MySignaturePanel from '../components/documentsReview/MySignaturePanel';
import Badge from '../components/ui/Badge';
import Tabs from '../components/ui/Tabs';
import FinanceSwitch from '../components/ui/FinanceSwitch';
import StateBlock from '../components/ui/StateBlock';
import { CheckCircle, Plus, Trash2 } from 'lucide-react';
import { getUserQuickReplies, addUserQuickReply, removeUserQuickReply, getSystemQuickReplies } from '../utils/quickReplies';
import { isPushSupported, isPushSubscribed, subscribeToPushDetailed, unsubscribeFromPush } from '../services/pushNotifications';

type ProfileTab = 'account' | 'notifications' | 'quickReplies' | 'blocklist' | 'claude';
const PROFILE_TABS: ProfileTab[] = ['account', 'notifications', 'quickReplies', 'blocklist'];
/** FIX-CLAUDE-B (D-314 §3): "חיבור ל-Claude" - OTE employees only. */
const EMPLOYEE_TABS: ProfileTab[] = [...PROFILE_TABS, 'claude'];

const initialsOf = (name?: string | null) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0)).join('').toUpperCase();

/** A flat settings section: title + optional description, then content. No card-in-card. */
function Section({ title, description, children, end }: { title: string; description?: string; children: React.ReactNode; end?: React.ReactNode }) {
  return (
    <section className="py-6 first:pt-0 last:pb-0">
      <div className={`flex items-start justify-between gap-4 ${children ? 'mb-4' : ''}`}>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-surface-900">{title}</h2>
          {description && <p className="text-sm text-surface-600 mt-0.5">{description}</p>}
        </div>
        {end}
      </div>
      {children}
    </section>
  );
}

/**
 * `embedded` (Q-DQ-114): the customer's "הפרופיל שלי" tab inside the portal - no page title of its own
 * (the portal's heading is used), the section tab lives in `?ptab=` so it does not collide with the
 * portal's own `?tab=`, and notification preferences are limited to the types a customer receives.
 */
export default function ProfilePage({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuthStore();
  const uid = useId();
  const fid = (n: string) => `${uid}-${n}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabKey = embedded ? 'ptab' : 'tab';
  const tabParam = searchParams.get(tabKey) as ProfileTab | null;
  // A customer (portal) identity - not an OTE employee, holds portal organizations.
  const isPortalUser = !!authUser && !authUser.is_ote_employee && (authUser.portal_orgs?.length ?? 0) > 0;
  // FIX-CLAUDE-B: "חיבור ל-Claude" for OTE employees only. DQ-108: quick replies and the personal blocklist
  // are OTE staff tools (the blocklist answers a customer 403).
  const visibleTabs = authUser?.is_ote_employee === true
    ? EMPLOYEE_TABS
    : isPortalUser ? PROFILE_TABS.filter((k) => k !== 'quickReplies' && k !== 'blocklist') : PROFILE_TABS;
  const tab: ProfileTab = tabParam && visibleTabs.includes(tabParam) ? tabParam : 'account';
  const setTab = (k: ProfileTab) => {
    const next = new URLSearchParams(searchParams);
    if (k === 'account') next.delete(tabKey); else next.set(tabKey, k);
    setSearchParams(next, { replace: true });
  };

  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
  });

  const profile = profileQuery.data?.data;
  // The account form never renders empty fields for a failed load (saving them would blank the profile).
  const profileFailed = profileQuery.isError && !profileQuery.data;

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (profile) {
      setName(profile.name);
      setPhone(profile.phone || '');
    }
    // Seed the form once per profile: a refetch of the same profile must not wipe unsaved edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const profileMutation = useMutation({
    mutationFn: () => api.updateProfile({ name, phone }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // DQ-006: the header, the user menu and the greeting read the signed-in
      // user from the auth store — refresh its name/phone, or they keep the old
      // name until the next sign-in.
      const saved = res?.data;
      if (saved) {
        useAuthStore.setState((s) => (s.user ? { user: { ...s.user, name: saved.name ?? s.user.name, phone: saved.phone ?? undefined } } : {}));
      }
      setSuccessMsg(t('profile.updated'));
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const passwordMutation = useMutation({
    mutationFn: () => api.updateProfile({ current_password: currentPassword, new_password: newPassword }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMsg(t('profile.passwordUpdated'));
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    passwordMutation.mutate();
  };

  const tabItems = visibleTabs.map((k) => ({ key: k, label: k === 'claude' ? t('claudeConnect.tab') : t(`uxFinance.profile.tabs.${k}`) }));
  const orgName = (profile as any)?.organization_name as string | undefined;
  // DQ-107 (N-04): a portal user's account role is "customer_viewer" for every rank, so the badge read "צופה"
  // even for the customer's manager. Show the portal rank, worded as in the portal's "משתמשים" screen.
  const portalRankKeys: string[] = isPortalUser
    ? Array.from(new Set((authUser!.portal_orgs ?? []).flatMap((o) => [
      o.portal_role === 'customer_portal_manager' ? 'portalP12.users.manager' : 'portalP12.users.employee',
      ...((o.permissions as Record<string, unknown> | undefined)?.approve_tickets === true ? ['portalP12.users.approver'] : []),
    ])))
    : [];

  return (
    <div className="max-w-3xl">
      {!embedded && <PageHeader title={t('profile.title')} />}

      {/* Identity — calm header: initials avatar, name, email, role. */}
      <div className="flex items-center gap-4 mb-6">
        <div className="avatar w-14 h-14 text-lg flex-shrink-0" aria-hidden="true">{initialsOf(profile?.name ?? authUser?.name)}</div>
        <div className="min-w-0">
          <p className="text-lg font-semibold text-surface-900 truncate">{profile?.name ?? authUser?.name}</p>
          <p className="text-sm text-surface-600 truncate"><bdi dir="ltr">{profile?.email ?? authUser?.email}</bdi></p>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {authUser && (portalRankKeys.length > 0
              ? portalRankKeys.map((k) => <Badge key={k} tone="brand">{t(k)}</Badge>)
              : <Badge tone="brand">{getUserRoleLabel(authUser.role, t)}</Badge>)}
            {orgName && <span className="text-xs text-surface-600">{orgName}</span>}
          </div>
        </div>
      </div>

      <Tabs items={tabItems} value={tab} onChange={setTab} ariaLabel={t('profile.title')} className="mb-6" />

      <div aria-live="polite">
        {successMsg && (
          <div className="flex items-center gap-2 mb-4 rounded-lg bg-success-50 px-4 py-3 text-sm text-success-800">
            <CheckCircle className="text-success-600 flex-shrink-0" size={18} aria-hidden="true" />
            <p>{successMsg}</p>
          </div>
        )}
      </div>

      <div role="tabpanel" aria-label={tab === 'claude' ? t('claudeConnect.tab') : t(`uxFinance.profile.tabs.${tab}`)} className="space-y-6">
        {tab === 'account' && (profileQuery.isLoading || profileFailed) && (
          <div className="card p-0">
            {profileQuery.isLoading ? (
              <StateBlock kind="loading" rows={3} />
            ) : (
              <StateBlock
                kind="error"
                title={t('states.profile.loadError')}
                error={profileQuery.error}
                onRetry={() => void profileQuery.refetch()}
                retrying={profileQuery.isFetching}
              />
            )}
          </div>
        )}

        {tab === 'account' && !profileQuery.isLoading && !profileFailed && (
          <div className="card divide-y divide-surface-200">
            <Section title={t('uxFinance.profile.personalDetails')}>
              <form onSubmit={(e) => { e.preventDefault(); profileMutation.mutate(); }} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={fid('name')} className="label">{t('common.name')}</label>
                    <input id={fid('name')} className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor={fid('phone')} className="label">{t('common.phone')}</label>
                    <input id={fid('phone')} className="input text-start" dir="ltr" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
                  </div>
                </div>
                <div>
                  <label htmlFor={fid('email')} className="label">{t('common.email')}</label>
                  <input id={fid('email')} className="input text-start" dir="ltr" value={profile?.email || ''} disabled aria-describedby={fid('email-note')} />
                  <p id={fid('email-note')} className="text-xs text-surface-600 mt-1">{t('profile.emailNote')}</p>
                </div>

                {profileMutation.error && (
                  <p role="alert" className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">
                    {(profileMutation.error as any)?.error?.message || t('profile.updateError')}
                  </p>
                )}

                <div className="flex justify-end max-sm:justify-start">
                  <button type="submit" disabled={profileMutation.isPending} className="btn btn-primary">
                    {profileMutation.isPending ? t('common.updating') : t('profile.updateProfile')}
                  </button>
                </div>
              </form>
            </Section>

            <EmployeePrivateDetails />

            <Section title={t('profile.changePassword')}>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="sm:w-1/2 sm:pe-2">
                  <label htmlFor={fid('cur')} className="label">{t('profile.currentPassword')}</label>
                  <input id={fid('cur')} type="password" dir="ltr" className="input" value={currentPassword} onChange={(e) => { setCurrentPassword(e.target.value); passwordMutation.reset(); }} required autoComplete="current-password" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={fid('new')} className="label">{t('profile.newPassword')}</label>
                    <input id={fid('new')} type="password" dir="ltr" className="input" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setMismatch(false); passwordMutation.reset(); }} required minLength={8} autoComplete="new-password" />
                  </div>
                  <div>
                    <label htmlFor={fid('confirm')} className="label">{t('profile.confirmPassword')}</label>
                    <input
                      id={fid('confirm')}
                      type="password"
                      dir="ltr"
                      className={`input ${mismatch ? 'input-error' : ''}`}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setMismatch(false); passwordMutation.reset(); }}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      aria-invalid={mismatch}
                      aria-describedby={mismatch ? fid('mismatch') : undefined}
                    />
                    {mismatch && <p id={fid('mismatch')} role="alert" className="error-text">{t('profile.passwordMismatch')}</p>}
                  </div>
                </div>

                {passwordMutation.error && (
                  <p role="alert" className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">
                    {(passwordMutation.error as any)?.error?.message || t('profile.changePasswordError')}
                  </p>
                )}

                <div className="flex justify-end max-sm:justify-start">
                  <button type="submit" disabled={passwordMutation.isPending} className="btn btn-secondary">
                    {passwordMutation.isPending ? t('common.updating') : t('profile.changePasswordBtn')}
                  </button>
                </div>
              </form>
            </Section>
          </div>
        )}

        {/* FIX-D327 (D-327 §2): the employee's own Google Calendar connection (hidden until the server is set up). */}
        {tab === 'account' && authUser?.is_ote_employee === true && <GoogleCalendarConnect />}
        {/* FIX-D337 (D-337 §2): "החתימה שלי" - the OTE manager signs documents with it. */}
        {tab === 'account' && authUser?.is_ote_employee === true && authUser?.role === 'admin' && (
          <div id="my-signature"><MySignaturePanel /></div>
        )}

        {/* P-30b (D-173): ONE "התראות" section — every type × bell / e-mail / push
            (e-mail off unless ticked). The legacy e-mail switches are gone. The
            push card below only connects THIS device to push. */}
        {tab === 'notifications' && (
          <>
            <NotificationPreferences audience={embedded ? 'portal' : undefined} />
            <div className="card">
              <PushNotificationSection />
            </div>
          </>
        )}

        {tab === 'quickReplies' && <div className="card"><QuickRepliesSection /></div>}

        {tab === 'blocklist' && <div className="card"><PersonalBlocklistSection /></div>}
        {tab === 'claude' && <ClaudeConnectPanel />}
      </div>
    </div>
  );
}

function PushNotificationSection() {
  const { t } = useTranslation();
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState<string | null>(null);

  useEffect(() => {
    const sup = isPushSupported();
    setSupported(sup);
    if (sup) {
      isPushSubscribed().then(setSubscribed);
    }
  }, []);

  const toggle = async () => {
    setLoading(true);
    setPushMessage(null);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
      } else {
        const res = await subscribeToPushDetailed();
        setSubscribed(res.ok);
        if (!res.ok) {
          setPushMessage(
            res.reason === 'denied' ? t('notificationPrefs.pushDenied')
              : res.reason === 'not_configured' ? t('notificationPrefs.pushNotConfigured')
                : res.reason === 'unsupported' ? t('notificationPrefs.pushUnsupported')
                  : t('notificationPrefs.saveError')
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // D-322 (6): "send me a test notice" - to every device of mine; says what happened.
  const sendTest = async () => {
    setTesting(true);
    setTestMessage(null);
    try {
      const r = (await api.sendTestPush()).data;
      setTestMessage(
        !r?.configured ? t('push.testNotConfigured')
          : r.devices === 0 ? t('push.testNoDevice')
            : t('push.testSent'),
      );
    } catch {
      setTestMessage(t('push.testFailed'));
    } finally {
      setTesting(false);
    }
  };

  if (!supported) return null;
  const denied = typeof Notification !== 'undefined' && Notification.permission === 'denied';

  return (
    <Section
      title={t('uxFinance.profile.pushTitle')}
      description={subscribed ? t('push.subscribed') : t('push.enableNotifications')}
      end={<FinanceSwitch checked={subscribed} onChange={() => toggle()} disabled={loading} label={t('uxFinance.profile.pushTitle')} />}
    >
      <p className="text-sm text-surface-700" data-testid="push-device-status">
        {denied ? t('push.testStatusDenied') : subscribed ? t('push.testStatusOn') : t('push.testStatusOff')}
      </p>
      {pushMessage ? <p role="alert" className="text-sm text-warning-700">{pushMessage}</p> : null}
      <div className="mt-2">
        <button type="button" className="btn btn-secondary btn-sm" onClick={sendTest} disabled={testing} data-testid="push-test-button">
          {t('push.testButton')}
        </button>
      </div>
      {testMessage ? <p role="status" className="text-sm text-surface-700 mt-2" data-testid="push-test-result">{testMessage}</p> : null}
    </Section>
  );
}

function QuickRepliesSection() {
  const { t } = useTranslation();
  const [customReplies, setCustomReplies] = useState(() => getUserQuickReplies());
  const [newText, setNewText] = useState('');
  const systemReplies = getSystemQuickReplies(t);

  const refresh = () => setCustomReplies(getUserQuickReplies());

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newText.trim()) return;
    addUserQuickReply(newText.trim());
    setNewText('');
    refresh();
  };

  const handleRemove = (id: string) => {
    removeUserQuickReply(id);
    refresh();
  };

  return (
    <Section title={t('quickReplies.manageTitle')} description={t('quickReplies.manageHint')}>
      <div className="space-y-5">
        <div>
          <p className="text-sm font-medium text-surface-700 mb-2">{t('quickReplies.builtin')}</p>
          <div className="flex flex-wrap gap-1.5">
            {systemReplies.map((r) => (
              <span key={r.id} dir="ltr" className="text-xs px-2 py-0.5 rounded-full bg-surface-100 text-surface-700 font-mono">
                /{r.slug}
              </span>
            ))}
          </div>
        </div>

        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2">
          <label htmlFor="qr-new" className="sr-only">{t('quickReplies.addPlaceholder')}</label>
          <input
            id="qr-new"
            className="input flex-1"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder={t('quickReplies.addPlaceholder')}
          />
          <button type="submit" className="btn btn-primary" disabled={!newText.trim()}>
            <Plus size={16} aria-hidden="true" /> {t('quickReplies.addButton')}
          </button>
        </form>

        {customReplies.length === 0 ? (
          <p className="text-sm text-surface-600">{t('quickReplies.emptyCustom')}</p>
        ) : (
          <ul className="divide-y divide-surface-200 border-y border-surface-200">
            {customReplies.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <span dir="ltr" className="text-xs font-mono text-surface-600">/{r.slug}</span>
                  <p className="text-sm text-surface-800 truncate">{r.text}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(r.id)}
                  aria-label={t('aria.delete')}
                  className="btn-icon text-surface-500 hover:text-danger-700 hover:bg-danger-50 transition-colors"
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}

function PersonalBlocklistSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [fieldName, setFieldName] = useState('phone');
  const [matchValue, setMatchValue] = useState('');
  const [matchType, setMatchType] = useState<BlocklistMatchType>('exact');
  const [reason, setReason] = useState('');

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['personal-blocklist'],
    queryFn: () => api.getPersonalBlocklistRules(),
  });

  const rules = data?.data || [];

  const createMutation = useMutation({
    mutationFn: () => api.createPersonalBlocklistRule({
      field_name: fieldName,
      match_value: matchValue,
      match_type: matchType,
      reason: reason || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-blocklist'] });
      setShowAdd(false);
      setMatchValue('');
      setReason('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deletePersonalBlocklistRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-blocklist'] });
      setDeleteId(null);
    },
  });

  const matchTypeLabels: Record<BlocklistMatchType, string> = {
    exact: t('blocklist.matchExact'),
    normalized_phone: t('blocklist.matchPhone'),
    domain: t('blocklist.matchDomain'),
  };

  return (
    <Section
      title={t('blocklist.personalTitle')}
      end={!showAdd && (
        <button type="button" onClick={() => setShowAdd(true)} className="btn btn-primary btn-sm">
          <Plus size={14} aria-hidden="true" />
          {t('blocklist.addRule')}
        </button>
      )}
    >
      {showAdd && (
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="mb-5 p-4 bg-surface-50 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label htmlFor="pb-field" className="label">{t('blocklist.fieldName')}</label>
              <select id="pb-field" className="input" value={fieldName} onChange={(e) => setFieldName(e.target.value)}>
                <option value="phone">phone</option>
                <option value="email">email</option>
                <option value="domain">domain</option>
              </select>
            </div>
            <div>
              <label htmlFor="pb-value" className="label">{t('blocklist.matchValue')} <span className="text-danger-700" aria-hidden="true">*</span></label>
              <input id="pb-value" className="input text-start" value={matchValue} onChange={(e) => setMatchValue(e.target.value)} placeholder={t('blocklist.valuePlaceholder')} dir="ltr" required />
            </div>
            <div>
              <label htmlFor="pb-type" className="label">{t('blocklist.matchType')}</label>
              <select id="pb-type" className="input" value={matchType} onChange={(e) => setMatchType(e.target.value as BlocklistMatchType)}>
                <option value="exact">{matchTypeLabels.exact}</option>
                <option value="normalized_phone">{matchTypeLabels.normalized_phone}</option>
                <option value="domain">{matchTypeLabels.domain}</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="pb-reason" className="label">{t('blocklist.reason')}</label>
            <input id="pb-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('blocklist.reasonPlaceholder')} />
          </div>
          {createMutation.error && (
            <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
              {(createMutation.error as any)?.error?.message || t('blocklist.createError')}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="btn btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
              {createMutation.isPending ? t('common.creating') : t('blocklist.addRule')}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <StateBlock kind="loading" rows={2} />
      ) : isError && !data ? (
        <StateBlock
          kind="error"
          compact
          title={t('states.profile.blocklistError')}
          error={error}
          onRetry={() => void refetch()}
          retrying={isFetching}
        />
      ) : rules.length === 0 ? (
        <p className="text-sm text-surface-600">{t('blocklist.noPersonalRules')}</p>
      ) : (
        <ul className="divide-y divide-surface-200 border-y border-surface-200">
          {rules.map((rule) => (
            <li key={rule.id} className="flex items-center justify-between gap-3 py-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                <span className="font-mono text-xs text-surface-600">{rule.field_name}</span>
                <bdi dir="ltr" className="font-mono text-sm text-surface-900 break-all">{rule.match_value}</bdi>
                <Badge>{matchTypeLabels[rule.match_type]}</Badge>
                {rule.reason && <span className="text-xs text-surface-600">{rule.reason}</span>}
              </div>
              <button
                type="button"
                onClick={() => setDeleteId(rule.id)}
                aria-label={t('blocklist.deleteRule')}
                className="btn-icon flex-shrink-0 text-surface-500 hover:text-danger-700 hover:bg-danger-50 transition-colors"
              >
                <Trash2 size={16} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {deleteId && (
        <ConfirmModal
          title={t('blocklist.deleteRule')}
          description={t('blocklist.deleteConfirm')}
          confirmLabel={t('common.delete')}
          onConfirm={() => deleteMutation.mutate(deleteId)}
          onCancel={() => setDeleteId(null)}
          danger
        />
      )}
    </Section>
  );
}
